import '../calendar/ui-calendar';
import { UIDateElement } from './ui-dateelement';
import {
    makePopoverHandler,
    appendModalControllerIfRequired,
} from '../../global/helpers';
import { TabIndex } from '../../global/keyboard';
import { Labels } from '../../global/labels';
import {
    dispatchNativeEvent,
    updateElement,
    redetachChildNodes,
    PopoverAlignment,
    getBaseGridPoint,
    addOutsideClickHandler,
    createElement,
    setNativeInputValue,
} from '../../global/ui-helpers';
import { browser } from '../../global/browser';
import { EventObserver } from '../../global/event-observer';
import { Alignment } from '../../global/enums';
import { Digest } from '../../global/digest';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { AttributeValue } from '../ui-element.type';
import type { ObservedAttributes, ElementConfig } from '../ui-element.type';
import type { NullOr } from '../../global/utils.type';
import type { AlignmentValue } from '../../global/constants';
import type { TranslationLabels } from '../../types/labels.type';
import type { UICalendar } from '../calendar/ui-calendar';
import type {
    UICalendarConfig,
    UICalendarRenderSource,
    UIDatePickerChangeSource,
} from '../calendar/ui-calendar.type';
import type { UIModal } from '../modal/ui-modal';
import type { UIData } from '../data/ui-data';
import type {
    UIDatePickerModalOffset,
    UIDatePickerAlignmentResolution,
} from './ui-datepicker.type';
import styles from './ui-datepicker.css';
import { ComponentState } from '../ui-element.const';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIDateElement}
 * @alias UIDatePicker
 * @element ui-datepicker
 * @implements {CustomFormComponent}
 * @classdesc Represents a class for <code>ui-datepicker</code> element.
 * Creates input field with opening dialog to pick date.
 * @property {string} value {@attr value} Value of datepicker.
 * @property {Locale} [locale="en"] {@attr locale} Localization rules in 4 character-format (e.g., "lt-LT").
 * @property {boolean} expanded {@attr expanded} If true, datepicker is expanded,
 * otherwise hidden.
 * @property {string} mobileTitle {@attr mobile-title} Title for mobile header.
 * @property {boolean} allowEmpty {@attr allow-empty} If true, empty values won't be replaced
 * with default date
 * @property {boolean} disabled {@attr disabled} If true, datepicker is disabled,
 * otherwise enabled. NB! this is only for make-up to disabled datepicker use
 * <code>datepicker.control.disabled = true</code>.
 * @property {string} [controlId] {@attr control-id} Control (input element) id. Deprecated! Instead of using control-id, please set id directly on the native input element instead. If no id is set, a random id is generated.
 * @property {AlignmentValue} [alignment] {@attr alignment}
 * Forced alignment for datepicker.
 * @property {HTMLInputElement} control Shortcut to control element.
 * @property {UICalendar} calendar Shortcut to calendar element.
 * @property {HTMLButtonElement} button Shortcut to calendar button if present.
 * @property {UIData} configSource Shortcut to data element.
 * @property {HTMLButtonElement} closeBtn Shortcut to close button element.
 * @property {HTMLButtonElement} clearBtn Shortcut to clean button if present.
 * @property {HTMLButtonElement} suffix Shortcut to the suffix container.
 * @slot
 * @example
 * <ui-datepicker>
 *   <input id="custom-datepicker-id" type="text" name="date" min="20.09.2018" max="25.10.2020" value="23.09.2018">
 * </ui-datepicker>
 */
@Component({ tag: 'ui-datepicker', styles })
export class UIDatePicker extends UIDateElement {
    /**
     * @type {ModalOffset}
     * @readonly
     * @private
     */
    private static readonly Offset: UIDatePickerModalOffset = {
        Vertical: getBaseGridPoint(false) * 6, // 48 input field height
        Horizontal: getBaseGridPoint() * -3,
    };

    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'expanded',
            'mobile-title',
            'locale',
            'control-id',
            'allow-empty',
            'disabled',
        ];
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-datepicker', {
            selectDate: 'Select date',
            selectedDate: 'Selected date',
            close: 'Close',
            clear: 'Clear input',
            invalidFormatHint: 'Use the dd.mm.yyyy format, like 24.12.2026',
            invalidFormatHintLt: 'Use the yyyy-mm-dd format, like 2026-12-24',
        });
    }

    @Attribute(String) defaultValue?: string;
    @Attribute(String) mobileTitle?: string;
    @Attribute(Boolean) expanded?: boolean;
    @Attribute(Boolean) disabled?: boolean;
    @Attribute(Boolean) allowEmpty?: boolean;
    @Attribute(String) controlId?: string;
    @Attribute(String) alignment?: AlignmentValue;
    @Attribute(Boolean) compact?: boolean;

    @Child('.ui-datepicker__input') control: HTMLInputElement = null!;
    @Child('.ui-datepicker__mobile-header') mobileHeader?: HTMLElement;
    @Child('ui-data') configSource?: UIData;
    @Child('.ui-datepicker__control button') button?: HTMLButtonElement;
    @Child('.ui-datepicker__clear button') clearBtn?: HTMLButtonElement;
    @Child('.ui-datepicker__suffix') suffix?: HTMLDivElement;

    private _id?: string;
    private _hasUserSetNativeId?: boolean;
    private _config?: UICalendarConfig;
    private _detachedPopover: NullOr<HTMLElement> = null;
    private outsideClickRemover?: () => void;
    private clearHandler?: EventListener;
    private togglePopoverHandler?: EventListener;
    private clickObserver?: EventObserver;
    private observer?: MutationObserver;

    get uid(): string {
        if (!this._id) {
            this._id = Digest.randomId();
        }
        return this._id;
    }

    /**
     * Priority: user-set id on input > control-id > generated uid.
     * @returns {string} The effective control id for ui-field (to set label[for] etc.)
     */
    getControlIdForField(): string {
        const controlIdAttr = this.controlId || this.getAttribute('control-id');
        const input =
            this.control ||
            (this.queryChildren('input')[0] as HTMLInputElement);
        if (input) {
            const inputId = input.getAttribute('id')?.trim();
            if (inputId) {
                return inputId;
            }
            if (controlIdAttr) {
                if (input.id !== controlIdAttr) {
                    input.id = controlIdAttr;
                    input.setAttribute('id', controlIdAttr);
                }
                return controlIdAttr;
            }
            const effectiveId = input.id || this.uid;
            if (input.id !== effectiveId) {
                input.id = effectiveId;
                input.setAttribute('id', effectiveId);
            }
            return effectiveId;
        }
        return controlIdAttr || this.uid;
    }

    get calendar(): UICalendar | null {
        if (this._detachedPopover) {
            return this._detachedPopover.querySelector<UICalendar>(
                'ui-calendar'
            );
        }
        return this.querySelector<UICalendar>('ui-calendar');
    }

    get modal(): NullOr<UIModal> {
        if (!this._detachedPopover) {
            return null;
        }
        return this._detachedPopover.closest<UIModal>('ui-modal');
    }

    /**
     * A convenience shortcut - returns the currently rendered suffix button, which is either a calendar button or a clear
     * button when allow-empty is active and a value is present.
     * @type {HTMLButtonElement | null}
     * @readonly
     */
    get suffixTrigger(): NullOr<HTMLButtonElement> {
        return this.button ?? this.clearBtn ?? null;
    }

    get value(): NullOr<string> {
        if (!this.isValidDate(this.control?.value ?? null, this.allowEmpty)) {
            return null;
        }
        return this.reformatDateString(this.control?.value ?? '');
    }

    set value(value: NullOr<string>) {
        const control =
            this.control ||
            (this.queryChildren('input')[0] as HTMLInputElement);
        if (!control || control.value === value) {
            return;
        }

        const reformattedValue = this.reformatDateString(value!);
        if (
            !this.isValidDate(reformattedValue) &&
            !(this.allowEmpty && reformattedValue === '')
        ) {
            setNativeInputValue(control, '');
            this.updateButtons();
            return;
        }

        setNativeInputValue(control, reformattedValue);
        this.updateButtons();
    }

    /**
     * Provides the date picker's value as a date object, when possible.
     * Mimics the native date input's `valueAsDate` property.
     * @type {Date | null}
     * @readonly
     */
    get valueAsDate(): NullOr<Date> {
        if (!this.isValidDate(this.control.value)) {
            return null;
        }

        const { year, month, date } = this.parseDateString(this.control.value);

        return new Date(year, month - 1, date);
    }

    /**
     * Checks that the datepicker's value can be editable, which means neither disabled nor read-only.
     * @type {boolean}
     * @readonly
     */
    get isEditable(): boolean {
        return !(this.control.readOnly || this.control.disabled);
    }

    /**
     * Checks if the datepicker's value is empty.
     * @type {boolean}
     * @readonly
     */
    get isEmpty(): boolean {
        return this.control.value.trim() == '';
    }

    /**
     * Checks if node is detached child.
     * @param {Node} node
     * @returns {boolean}
     */
    isDetachedChild(node: Node): boolean {
        return !!this._detachedPopover?.contains(node);
    }

    /**
     * Closes datepicker popover.
     */
    closeDetachedPopover(): void {
        const modal = this.modal; // request only once
        if (modal && this._detachedPopover) {
            this._detachedPopover = null;
            modal.close();
        }
    }

    /**
     * Opens detached popover.
     * @fires event:modal-open
     */
    openDetachedPopover(): void {
        if (this._detachedPopover) {
            return;
        }

        // If control is readonly do not open calendar.
        if (this.control.readOnly) {
            this.expanded = false;
            return;
        }

        this._detachedPopover = this.createElement(this.renderPopover());
        this.prerenderElementsTree.call(this._detachedPopover, true);
        if (this._config) {
            this.calendar!.setConfig(this._config);
        }
        this.calendar!.updateControlValue(
            this.value ?? '',
            'ui-datepicker' as UICalendarRenderSource
        );
        updateElement(this.calendar!, {
            attributes: {
                locale: this.locale,
            },
            events: {
                change: this.handleChangeCalendarValue.bind(this),
            },
        });
        const closeBtn = this._detachedPopover.querySelector<HTMLButtonElement>(
            '.ui-datepicker__close-btn'
        );
        const mobileCloseBtn =
            this._detachedPopover.querySelector<HTMLButtonElement>(
                '.ui-datepicker__close-action'
            );
        [closeBtn, mobileCloseBtn].forEach((button) =>
            button?.addEventListener(
                'click',
                this.closeDetachedPopover.bind(this)
            )
        );
        appendModalControllerIfRequired();
        this.dispatchCustomEvent('modal-open', {
            type: !browser.mobileMediaMatches() ? 'menu' : undefined,
            content: this._detachedPopover,
            params: {
                classList: {
                    '-datepicker': true,
                },
                position: browser.mobileMediaMatches()
                    ? null
                    : makePopoverHandler(this.control, {
                          popoverHeight: () =>
                              this.calendar ? this.calendar.offsetHeight : 0,
                          popoverWidth: () =>
                              this.calendar ? this.calendar.offsetWidth : 0,
                          verticalOffset:
                              this.alignment === PopoverAlignment.Top ||
                              this.control.closest('ui-modal')
                                  ? UIDatePicker.Offset.Vertical
                                  : 0,
                          horizontalOffset: () => {
                              const isSubModal =
                                  this.modal?.classList.contains('-sub-modal');
                              return browser.mobileMediaMatches() && isSubModal
                                  ? UIDatePicker.Offset.Horizontal
                                  : 0;
                          },
                          ...this.resolveAlignment(),
                      }),
                target: this,
                onOpen: () => {
                    if (this._detachedPopover) {
                        let dateBtn =
                            this._detachedPopover.querySelector<HTMLElement>(
                                '[data-date="' + this.value + '"]'
                            );
                        if (!dateBtn) {
                            dateBtn = this.calendar!.today || null;
                        }
                        if (!dateBtn) {
                            dateBtn =
                                this.calendar!.activeDayButtons[
                                    this.calendar!.activeDayButtons.length - 1
                                ];
                            dateBtn.tabIndex = parseInt(TabIndex.Active, 10);
                        }
                        this.calendar!.monthPicker.recalculateMonthWidth();
                        dateBtn && requestAnimationFrame(() => dateBtn.focus());
                    }
                },
                onClose: () => {
                    requestAnimationFrame(() => {
                        this.suffixTrigger?.focus({ preventScroll: true });
                    });
                    this.handleExternalBlurChange();
                    this.expanded = false;
                },
                attributes: {
                    dynamic: true,
                    compact: true,
                },
            },
        });
    }

    /**
     * Allows external frameworks like Angular to be triggered on blur event.
     * It is rarely used, but sometimes is needed.
     * @private
     */
    private handleExternalBlurChange(): void {
        this.control?.dispatchEvent(new Event('blur'));
    }

    /**
     * @private
     */
    private resolveAlignment():
        | UIDatePickerAlignmentResolution
        | Record<string, never> {
        if (!this.alignment) {
            return {};
        }
        return {
            supportMiddleAlignment: this.alignment === PopoverAlignment.Middle,
            supportTopAlignment: this.alignment === PopoverAlignment.Top,
            supportBottomAlignment: this.alignment === PopoverAlignment.Bottom,
            verticalAxisSide:
                this.alignment === PopoverAlignment.Top
                    ? Alignment.Top
                    : Alignment.Bottom,
        };
    }

    /**
     * Fires callback when the popover state has been changed.
     * @private
     */
    private onChangePeriod(): void {
        if (this.calendar) {
            this.calendar.min = this.min;
            this.calendar.max = this.max;
        }
    }

    /**
     * Fires callback when the popover state has been changed.
     * @private
     */
    private onChangePopoverState(): void {
        if (this.expanded) {
            this.openDetachedPopover();
            void addOutsideClickHandler(
                this,
                this.handleClickOutside.bind(this)
            ).then((func) => (this.outsideClickRemover = func));
        } else {
            this.closeDetachedPopover();
            this.outsideClickRemover?.();
        }
    }

    /**
     * Fires callback when user clicked outside of element.
     * @param {Event} [event]
     * @private
     */
    private handleClickOutside(event?: Event): void {
        if (!event) {
            return;
        }
        if (!this.expanded) {
            return;
        }
        if (
            this.contains(event.target as Node) ||
            this.isDetachedChild(event.target as Node)
        ) {
            return;
        }
        this.suffixTrigger?.focus({
            preventScroll: true,
        });
        this.expanded = false;
    }

    /**
     * Fires callback when control button is clicked.
     * @private
     */
    private handleTogglePopover(): void {
        this.expanded = !this.expanded;
    }

    /**
     * Fires callback when the calendar's value is updated.
     * @param {Event | CustomEvent} [event]
     * @private
     */
    private handleChangeCalendarValue(
        event?: Event | CustomEvent<unknown>
    ): void {
        const calendar = event?.target as UICalendar | null;
        if (!calendar) {
            return;
        }
        if (this.calendar && calendar !== this.calendar) {
            return;
        }

        const detail = (event as CustomEvent<UIDatePickerChangeSource>)?.detail;
        // Only commit the value when the user explicitly selects a date
        // (click or Enter/Space). Navigation events (arrow keys, prev/next
        // month, month-picker change) no longer fire change events, but guard
        // here as a safety net.
        if (detail?.source !== 'date-selected') {
            return;
        }

        setNativeInputValue(this.control, calendar.value!);
        dispatchNativeEvent(this.control, 'input', true);
        if (this.control.willValidate && this.control.required) {
            dispatchNativeEvent(this.control, 'invalid', true);
        }

        // Close the popup immediately after the user confirms a date.
        this.expanded = false;
    }

    /**
     * Fires callback when the input's value is updated.
     * @private
     */
    private handleChangeInputValue(): void {
        const value = this.control.value;
        let nextValue =
            this.isEmpty && this.allowEmpty
                ? ''
                : this.isValidDate(value)
                  ? this.parseDateValue(value, this.min, this.max).value
                  : value;

        if (!this.isValidDate(nextValue) && !this.isEmpty) {
            nextValue = this.control.value;
            dispatchNativeEvent(this.control, 'invalid', true);
        }
        if (this.control.value !== nextValue) {
            setNativeInputValue(this.control, nextValue);
            dispatchNativeEvent(this.control, 'change', true);
        }
        if (this.calendar && nextValue && this.isValidDate(nextValue)) {
            this.calendar.value = nextValue;
            this.calendar.updateControlValue(nextValue);
        }
        this.updateButtons();
    }

    /**
     * Fires callback when the input control gets an input event.
     * @private
     */
    private handleInputValue(): void {
        const date = this.valueAsDate;

        if (!date || !this.isDateInRange(date)) {
            return;
        }

        if (this.button) {
            updateElement(this.button, {
                attributes: {
                    'aria-label': this.resolveSelectedDateStr(),
                },
            });
        }

        this.handleChangeInputValue();
    }

    /**
     * Fires callback when the input control gets a focus event.
     * @param {Event} e
     * @private
     */
    private handleFocusInput(e: Event): void {
        if (e.isTrusted && !this.expanded) {
            this.expanded = true;
        }
    }

    /**
     * Handles clean event.
     * @private
     */
    private handleClear(): void {
        if (!this.allowEmpty) {
            return;
        }
        setNativeInputValue(this.control, '');
        dispatchNativeEvent(this.control, 'change', true);
        dispatchNativeEvent(this.control, 'input', true);
        this.control.focus();
    }

    /**
     * @inheritDoc
     */
    handleControlMutations(mutations: Array<MutationRecord>): void {
        for (const mutation of mutations) {
            if (mutation.type !== 'attributes') {
                continue;
            }
            if (['value', 'min', 'max'].includes(mutation.attributeName!)) {
                if (
                    mutation.oldValue !==
                    (mutation.target as HTMLElement).getAttribute(
                        mutation.attributeName!
                    )
                ) {
                    this.onChangePeriod();
                    return;
                }
            }
            if (mutation.attributeName === 'disabled') {
                const isDisabled = (mutation.target as HTMLInputElement)
                    .disabled;
                this.disabled = isDisabled;
                if (this.rendered) {
                    this.updateButtons();
                }
                if (isDisabled) {
                    this.expanded = false;
                }
            }
        }
    }

    /**
     * @inheritDoc
     */
    observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        switch (name) {
            case 'expanded':
                this.onChangePopoverState();
                break;
            case 'control-id':
                if (
                    this.rendered &&
                    this.control &&
                    !this._hasUserSetNativeId
                ) {
                    const effectiveId =
                        this.controlId ||
                        this.getAttribute('control-id') ||
                        this.uid;
                    this.control.id = effectiveId;
                    this.control.setAttribute('id', effectiveId);
                }
                break;
            case 'allow-empty':
                if (this.state !== ComponentState.Hydrated) {
                    return;
                }

                this.updateButtons();
                break;
            case 'disabled': {
                if (this.rendered && this.control) {
                    const isDisabled = newValue !== null;
                    this.onDisabledStateChanged(isDisabled);
                    this.updateButtons();
                    if (isDisabled) {
                        this.expanded = false;
                    }
                }
                break;
            }
            default:
                break;
        }
    }

    /**
     * Updates the suffix buttons.
     */
    updateButtons(): void {
        this.suffix!.replaceChildren(
            createElement(
                this.allowEmpty && this.control.value
                    ? this.renderClearButton()
                    : this.renderCalendarButton()
            )!
        );
    }

    /**
     * Resolves the regexp pattern.
     * @returns {string}
     * @private
     */
    private resolvePattern(): string {
        return UIDatePicker.LithuanianLocales.includes(this.locale)
            ? '[0-9]{4}-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])'
            : '(0[1-9]|[12][0-9]|3[01])\\.(0[1-9]|1[0-2])\\.[0-9]{4}';
    }

    /**
     * Returns popover calendar content config.
     * @returns {ElementConfig}
     * @private
     */
    private renderPopover(): ElementConfig {
        return {
            tagName: 'div',
            classList: {
                'ui-datepicker__popover': true,
            },
            attributes: {
                'role': 'dialog',
                'aria-label': UIDatePicker.labels.selectDate,
            },
            children: [
                ...(browser.mobileMediaMatches()
                    ? [this.buildMobileHeader()]
                    : []),
                {
                    tagName: 'ui-calendar',
                    attributes: {
                        locale: this.locale || UIDatePicker.LOCALE,
                    },
                    children: [
                        {
                            tagName: 'input',
                            attributes: {
                                type: 'hidden',
                                value: this.value,
                                min: this.min,
                                max: this.max,
                            },
                        },
                    ],
                },
                ...(!browser.mobileMediaMatches()
                    ? [this.buildCloseButton()]
                    : []),
            ],
        };
    }

    /**
     * Builds close button for popover.
     * @private
     * @returns {ElementConfig}
     */
    private buildCloseButton(): ElementConfig {
        return {
            tagName: 'ui-buttonbar',
            children: [
                {
                    tagName: 'ui-button',
                    attributes: {
                        layout: 'secondary',
                    },
                    classList: {
                        'ui-datepicker__close-btn': true,
                    },
                    children: [this.getLabel('close')],
                },
            ],
        };
    }

    /**
     * Builds datepicker header for mobile popover.
     * @private
     * @returns {ElementConfig}
     */
    private buildMobileHeader(): ElementConfig {
        const closeButton: ElementConfig = {
            tagName: 'div',
            classList: {
                'ui-datepicker__close-action-container': true,
            },
            children: [
                ...(!this.compact
                    ? [
                          {
                              tagName: 'ui-button',
                              classList: {
                                  'ui-datepicker__close-action': true,
                              },
                              attributes: {
                                  'glyph': 'redesign-x-mark',
                                  'layout': 'tertiary',
                                  'aria-label': UIDatePicker.labels.close,
                              },
                          } as ElementConfig,
                      ]
                    : []),
            ],
        };

        const title: ElementConfig = {
            tagName: 'div',
            classList: {
                'ui-datepicker__title-container': true,
            },
            children: [
                {
                    tagName: 'span',
                    classList: {
                        'ui-datepicker__title': true,
                    },
                    children: [
                        this.mobileTitle || UIDatePicker.labels.selectDate,
                    ],
                },
            ],
        };

        return {
            tagName: 'header',
            classList: {
                'ui-datepicker__mobile-header': true,
            },
            children: [title, closeButton],
        };
    }

    /**
     * Returns clear button config.
     * This button is used to clear the input value.
     * @returns {ElementConfig}
     * @private
     */
    private renderClearButton(): ElementConfig {
        return {
            tagName: 'ui-button',
            classList: {
                'ui-datepicker__clear': true,
            },
            attributes: {
                'layout': 'tertiary',
                'glyph': 'redesign-circle-x-mark',
                'aria-label': UIDatePicker.labels.clear,
                'disabled': this.hasAttribute('disabled'),
                'data-event': 'ui-datepicker-clear',
            },
        };
    }

    /**
     * Returns calendar button config.
     * This button is used to open calendar popover.
     * @returns {ElementConfig}
     */
    renderCalendarButton(): ElementConfig {
        return {
            tagName: 'ui-button',
            classList: {
                'ui-datepicker__control': true,
            },
            attributes: {
                'layout': 'tertiary',
                'glyph': 'redesign-calendar-days',
                'aria-label': this.resolveSelectedDateStr(),
                'disabled': this.hasAttribute('disabled'),
                'data-event': 'ui-datepicker-popover',
            },
        };
    }

    /**
     * Resolves accessibility selected date string.
     * @returns {string}
     * @private
     */
    private resolveSelectedDateStr(): string {
        let str = UIDatePicker.labels.selectDate as string;
        if (this.value) {
            str +=
                ', ' +
                (UIDatePicker.labels.selectedDate as string) +
                ' ' +
                this.value;
        }
        return str;
    }

    /**
     * @inheritDoc
     */
    getControlElement(): HTMLInputElement | undefined {
        return this.control;
    }

    /**
     * @inheritDoc
     */
    onValuePatched(newValue: unknown): void {
        if (!this.isValidDate(newValue as string, this.allowEmpty)) {
            return;
        }
        const reformatted = this.reformatDateString(newValue as string);
        setNativeInputValue(this.control, reformatted);
        dispatchNativeEvent(this.control, 'input', true);
    }

    /**
     * @inheritDoc
     */
    onDisabledStateChanged(newState: boolean): void {
        this.control.disabled = newState;
    }

    /**
     * @inheritDoc
     */
    onReadonlyStateChanged(newState: boolean): void {
        this.control.readOnly = newState;
    }

    /**
     * After hydration, the input may already have a value set externally (e.g., by Angular's
     * ReactiveFormsModule) before the web component rendered and patched the value setter.
     * In that case `onValuePatched` was never called, so the value may still be an unresolved
     * alias like "today". Detect this and reformat the value so the host framework sees the
     * real date string.
     * @private
     */
    private reformatExternallySetValue(): void {
        const currentValue = this.control.value;
        if (!currentValue) {
            return;
        }
        const reformatted = this.reformatDateString(currentValue);
        if (reformatted && reformatted !== currentValue) {
            setNativeInputValue(this.control, reformatted);
            dispatchNativeEvent(this.control, 'input', true);
        }
    }

    /**
     * Adds event listeners to the component.
     * @private
     */
    private addEventListeners(): void {
        this.observer!.observe(this.control, { attributes: true });
        this.clickObserver!.observe(this);

        this.addEventListener('ui-datepicker-clear', this.clearHandler!);
        this.addEventListener(
            'ui-datepicker-popover',
            this.togglePopoverHandler!
        );
    }

    /**
     * Removes event listeners from the component.
     * @private
     */
    private removeEventListeners(): void {
        this.observer!.disconnect();
        this.clickObserver!.disconnect();
        this.removeEventListener('ui-datepicker-clear', this.clearHandler!);
        this.removeEventListener(
            'ui-datepicker-popover',
            this.togglePopoverHandler!
        );
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        const control =
            (this.queryChildren('input')[0] as HTMLInputElement) ||
            document.createElement('input');
        if (control) {
            control.classList.add('ui-datepicker__input');
            ['value', 'min', 'max'].forEach((a) => {
                if (control.hasAttribute(a)) {
                    control.setAttribute(
                        a,
                        this.reformatDateString(
                            (this as unknown as Record<string, string>)[a]
                        )
                    );
                }
            });
            if (control.hasAttribute('disabled')) {
                this.setAttribute('disabled', '');
            }
        }

        const userSetNativeId = control.getAttribute('id');
        if (this._hasUserSetNativeId === undefined) {
            this._hasUserSetNativeId = !!userSetNativeId?.trim();
        }
        const effectiveId = this._hasUserSetNativeId
            ? userSetNativeId
            : this.controlId || this.getAttribute('control-id') || this.uid;
        if (!this._hasUserSetNativeId) {
            control.setAttribute('id', effectiveId!);
        }

        const pattern = this.resolvePattern();
        this.insertElements([
            {
                tagName: 'input',
                element: control,
                attributes: {
                    'type': 'text',
                    'pattern': pattern,
                    'autocomplete': 'off',
                    'aria-autocomplete': 'none',
                    'id': effectiveId,
                    'data-pattern-error':
                        UIDatePicker.LithuanianLocales.includes(this.locale)
                            ? UIDatePicker.labels.invalidFormatHintLt
                            : UIDatePicker.labels.invalidFormatHint,
                },
                classList: {
                    'ui-datepicker__input': true,
                },
            },
            {
                tagName: 'div',
                classList: {
                    'ui-datepicker__suffix': true,
                    '-input-decorator': true,
                },
                children: [
                    ...(this.allowEmpty && control.value
                        ? [this.renderClearButton()]
                        : [this.renderCalendarButton()]),
                ],
            },
        ]);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        updateElement(this.control, {
            events: {
                change: this.handleChangeInputValue.bind(this),
                input: this.handleInputValue.bind(this),
            },
        });

        this.clearHandler = this.handleClear.bind(this);
        this.togglePopoverHandler = this.handleTogglePopover.bind(this);
        this.clickObserver = new EventObserver({ name: 'click' });
        this.observer = new MutationObserver(
            this.handleControlMutations.bind(this)
        );

        this.addEventListeners();
        this.reformatExternallySetValue();

        if (this.configSource) {
            redetachChildNodes(this);
            this.configSource.get((config: unknown) => {
                this._config = config as UICalendarConfig;
                if (this.calendar) {
                    this.calendar.setConfig(config as UICalendarConfig);
                }
            });
        }
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.removeEventListeners();
        this.closeDetachedPopover();
        this.outsideClickRemover?.();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.addEventListeners();
    }
}
